import { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import IcImage from '../../assets/images/svg/ic-image.svg';
import Dropdown from './Dropdown';
import DefaultTamplatePNG from '../../assets/images/png/img-default-tamplate-img@3x.png';
import { color } from '../../app/theme';
import IconDelete from '../../assets/images/Icons/IconDelete';
import IcBrowse from '../../assets/images/svg/ic-browse.svg';
import FileService from '../../services/FileService';
import { SnackBarType } from '../../app/types';
import { enqueueSnackbar } from '../../features/notifierSlice';
import { validateHttpsPath, validateFileName } from '../../app/utils';

const useStyles = makeStyles(
  () => ({
    root: { marginBottom: 8 },
    operation: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8,
    },
    dropdown: {
      width: '100%',
      marginLeft: 6,
    },
    URLInputContainer: {
      marginBottom: 8,
    },
    fileChooser: {
      cursor: 'pointer',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      height: 44,
      fontSize: 15,
      color: color.$3,
      lineHeight: 1,
      border: `solid 1px ${color.$10}`,
      borderRadius: 22,
      backgroundColor: color.$7,
      padding: '0 16px',
      '& > img': {
        position: 'absolute',
        top: 10,
        right: 8,
      },
    },
    'fileChooser--error': {
      borderColor: color.$17,
    },
    URLInput: {
      display: 'block',
      width: '100%',
      height: 44,
      border: `1px solid ${color.$10}`,
      borderRadius: 22,
      outline: 'none',
      padding: '0 16px',
      '&:focus': {
        borderColor: color.$1,
      },
    },
    'URLInput--error': {
      borderColor: color.$17,
    },
    preview: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      backgroundImage: `url(${DefaultTamplatePNG})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      '& > img': {
        maxWidth: '100%',
        height: '100%',
      },
    },
    'preview--type-url': {
      width: 180,
      height: 180,
    },
    deleteIcon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 6,
      right: 6,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 24,
      height: 24,
      border: `solid 1px ${color.$9}`,
      borderRadius: '50%',
      backgroundColor: color.$7,
      '& > svg': {
        display: 'block',
      },
    },
    remind: {
      fontSize: 13,
      lineHeight: '18px',
      color: color.$11,
    },
    'remind--error': {
      color: color.$17,
    },
    image: {
      objectFit: 'cover',
      width: '100%',
    },
    'image--type-url': {
      width: '100%',
      height: '100%',
    },
  }),
  { name: 'UploadImage' },
);

function validateFile(file) {
  const name = file?.name ? file.name.slice(0, file.name.lastIndexOf('.')) : '';

  return name && validateFileName(name);
}

function validateImage(image) {
  const result = {
    pass: false,
    aspectRatio: '1:1',
  };

  if (image.width === 960) {
    switch (image.height) {
      case 1553:
        result.pass = true;
        result.aspectRatio = '1:1.618';
        return result;
      case 960:
        result.pass = true;
        result.aspectRatio = '1:1';
        return result;
      case 500:
        result.pass = true;
        result.aspectRatio = '1.92:1';
        return result;
      default:
        return result;
    }
  }
  return result;
}

function UploadImage({
  imageType,
  imageUrl,
  aspectRatio,
  index,
  onChange,
  onlyUpload,
}) {
  const classes = useStyles();
  const fileRef = useRef(null);
  const dispatch = useDispatch();
  const [localImageType, setLocalImageType] = useState(imageType);
  const [localURL, setLocalURL] = useState(imageUrl);
  const [showImage, setShowImage] = useState(false);
  const [preImageObj, setPreImageObj] = useState({
    type: imageType,
    url: imageUrl,
    aspectRatio,
  });
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);
  const [isFileNameError, setFileNameIsError] = useState(false);
  const [isGoodsImageUrlError, setIsGoodsImageUrlError] = useState(false);

  function handleChangeImageType(type) {
    setLocalImageType(type);
    if (localURL !== '') {
      setPreImageObj({
        type: preImageObj.type,
        url: localURL,
        aspectRatio: '1:1',
      });
    }
    if (preImageObj.url !== '' && type === preImageObj.type) {
      setLocalURL(preImageObj.url);
    } else {
      setLocalURL('');
    }
    if (type === 'url') {
      setShowImage(true);
    }
    onChange({
      imageType: type,
      imageUrl: '',
      aspectRatio: '1:1',
    });
  }

  function handleChange(e) {
    const url = e.target.value;
    const img = new Image();
    img.onload = () => {
      setShowImage(true);
      onChange({
        imageType: localImageType,
        imageUrl: url,
        aspectRatio: '1:1',
      });
    };
    img.src = url;
    setShowImage(false);
    setLocalURL(url);
  }

  function handleClear() {
    setIsError(false);
    setFileNameIsError(false);
    setLocalURL('');
    if (localImageType === 'upload') {
      setShowImage(false);
    }
    onChange({
      imageType: localImageType,
      imageUrl: '',
      aspectRatio: '1:1',
    });
  }

  function handleOpenFileChoooser() {
    fileRef.current.click();
  }

  async function upload(file, imageAspectRatio) {
    try {
      const typeReg = /^(?<mimeType>image)\/.*/;
      const groupResult = typeReg.exec(file.type);

      if (!(groupResult.groups && groupResult.groups.mimeType)) {
        throw new Error('File Type Error');
      }

      const response = await FileService.upload(file);
      setLocalURL(response.url);
      setShowImage(true);
      onChange({
        imageType: localImageType,
        imageUrl: response.url,
        aspectRatio: imageAspectRatio,
      });
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error.message,
        }),
      );
    }
  }

  function handleChooseFile(e) {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = () => {
        const result = validateImage(image);
        const isFileVerify = validateFile(file);
        if (!result.pass || !isFileVerify) {
          setIsError(!result.pass);
          setFileNameIsError(!isFileVerify);
        } else {
          upload(file, result.aspectRatio);
        }
      };
      image.src = url;
    }
  }

  useEffect(() => {
    setIsGoodsImageUrlError(
      imageType === 'url' && !validateHttpsPath(imageUrl),
    );
  }, [imageType, imageUrl]);

  useEffect(() => {
    if (localImageType !== imageType) {
      setLocalImageType(imageType);
    }
  }, [imageType]);

  useEffect(() => {
    if (localURL !== imageUrl) {
      setLocalURL(imageUrl);
    }
  }, [imageUrl]);

  useEffect(() => {
    if (localURL !== '') {
      setShowImage(true);
    }
  }, [localURL, localImageType]);

  return (
    <div className={classes.root}>
      {!onlyUpload && (
        <div className={classes.operation}>
          <img src={IcImage} alt="Upload" />
          <Dropdown
            className={classes.dropdown}
            menuItems={[
              {
                text: t('templates.image-upload-type.upload'),
                value: 'upload',
              },
              { text: t('templates.image-upload-type.url'), value: 'url' },
            ]}
            value={localImageType}
            onChange={handleChangeImageType}
          />
        </div>
      )}

      <div className={classes.URLInputContainer}>
        {localImageType === 'upload' && localURL === '' && (
          <div
            className={classnames(classes.fileChooser, {
              [classes['fileChooser--error']]: isError || isFileNameError,
            })}
            onClick={handleOpenFileChoooser}
          >
            {t('templates.chose-file')}
            <img src={IcBrowse} alt="Select File" />
            <input
              ref={fileRef}
              type="file"
              hidden
              accept=".jpg,.jpeg,.png,.gif"
              onChange={handleChooseFile}
            />
          </div>
        )}
        {localImageType === 'url' && (
          <input
            className={classnames(classes.URLInput, {
              [classes['URLInput--error']]: isGoodsImageUrlError,
            })}
            type="text"
            value={localURL}
            placeholder={t('templates.placeholder-url')}
            onChange={handleChange}
          />
        )}
      </div>
      {showImage && !(localImageType === 'upload' && localURL === '') && (
        <div
          className={classnames(classes.preview, {
            [classes['preview--type-url']]: localImageType === 'url',
          })}
        >
          <div className={classes.deleteIcon} onClick={handleClear}>
            <IconDelete
              id={`icon-delete-${index}`}
              width={20}
              height={20}
              color={color.$11}
              hoverColor={color.$11}
            />
          </div>
          {localURL && localURL !== '' && (
            <img
              className={classnames(classes.image, {
                [classes['image--type-url']]: localImageType === 'url',
              })}
              src={localURL}
              alt="preview"
            />
          )}
        </div>
      )}
      {!localURL && localImageType !== 'url' && (
        <>
          <div
            className={classnames(classes.remind, {
              [classes['remind--error']]: isError,
            })}
          >
            {t('templates.upload-image-remind')}
          </div>
          {isFileNameError && (
            <div
              className={classnames(classes.remind, classes['remind--error'])}
              style={{
                marginTop: 4,
              }}
            >
              {t('templates.upload-image-remind-2')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

UploadImage.defaultProps = {
  imageType: 'upload',
  imageUrl: '',
  onChange: () => {},
  aspectRatio: '1:1',
};

UploadImage.propTypes = {
  imageType: PropTypes.oneOf(['upload', 'url']),
  imageUrl: PropTypes.string,
  aspectRatio: PropTypes.oneOf(['1:1', '1:1.618', '1.92:1']),
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func,
};

export default UploadImage;
